import { useCurrentUser } from "@/features/auth/hooks";
import { holds } from "@/features/auth/permissions";
import { getProjectSlug } from "@/lib/api";
import { leaveProject } from "@/lib/project-url";
import { useApiMutation, useApiQuery } from "@/lib/query";
import type {
  Invitee,
  Member,
  MemberCreate,
  Project,
  ProjectCreate,
  ProjectDetail,
  ProjectRole,
  ProjectUpdate,
} from "./types";

/** `enabled: false` until needed: the switcher only lists them once opened (or hovered). */
export const useProjects = (enabled = true) => useApiQuery<Project[]>("/projects", {}, { enabled });

export const useCreateProject = () =>
  useApiMutation<ProjectDetail, ProjectCreate>("post", "/projects", { invalidate: [["/projects"]] });

/**
 * The project this page is in, loaded once and kept: its name, the caller's
 * role and `can("members:write")` (wildcards honoured, so `members:*` counts). Outside a project
 * (`/`, the first-project screen, tests that set none) it never fetches.
 */
export function useProject() {
  const slug = getProjectSlug();
  const query = useApiQuery<ProjectDetail>(
    `/projects/${slug ?? ""}`,
    {},
    { enabled: slug !== null, staleTime: 5 * 60_000 },
  );
  const permissions = query.data?.permissions ?? [];
  return { slug, query, project: query.data, can: (permission: string) => holds(permissions, permission) };
}

const projectPath = () => `/projects/${getProjectSlug() ?? ""}`;

export const useUpdateProject = () =>
  useApiMutation<ProjectDetail, ProjectUpdate>("patch", projectPath(), { invalidate: ["/projects"] });

export const useDeleteProject = () => useApiMutation<void, void>("delete", () => projectPath());

const membersPath = () => `${projectPath()}/members`;

export const useMembers = (enabled = true) => useApiQuery<Member[]>(membersPath(), {}, { enabled });

export const useAddMember = () =>
  useApiMutation<Member, MemberCreate>("post", membersPath(), { invalidate: [membersPath()] });

export const useChangeMemberRole = () =>
  useApiMutation<Member, { email: string; role: ProjectRole }>(
    "patch",
    ({ email }) => `${membersPath()}/${encodeURIComponent(email)}`,
    // Their role may be yours: the project (and its permissions) refetch too.
    { body: ({ role }) => ({ role }), invalidate: [projectPath()] },
  );

/** Removing yourself is leaving: out of the project to `/`, with a toast. */
export const useRemoveMember = () => {
  const email = useCurrentUser().data?.email;
  const { slug, project } = useProject();
  return useApiMutation<void, string>(
    "delete",
    (removed) => `${membersPath()}/${encodeURIComponent(removed)}`,
    {
      invalidate: [projectPath()],
      onSuccess: (_, removed) => {
        if (removed === email) leaveProject(slug, `You left ${project?.name ?? "the project"}.`);
      },
    },
  );
};

/** Take yourself out of this project (`useRemoveMember` then leaves it). */
export function useLeaveProject() {
  const email = useCurrentUser().data?.email;
  const remove = useRemoveMember();
  const leave = (onError: (e: Error) => void) => email && remove.mutate(email, { onError });
  return { leave, pending: remove.isPending };
}

/** People already signed in to the workbench, for the add-member suggestions. */
export const useInvitees = (enabled = true) =>
  useApiQuery<Invitee[]>(`${projectPath()}/invitees`, {}, { enabled });

/**
 * Only the last active owner may not leave or lose the role: a pending owner
 * (not signed in yet) does not count.
 */
export function activeOwners(members: Member[] | undefined): number {
  return members?.filter((m) => m.role === "owner" && m.is_active && !m.pending).length ?? 0;
}
